import { useState } from 'react';
import { useMutation } from '@tanstack/react-query';
import { useTranslation } from 'react-i18next';
import type {
  MemberOpeningDecision,
  MemberOpeningPreview,
  MemberOpeningResult,
} from '@pokerroom/types';
import { api } from '../../lib/api';
import { ALERT, CHECK_LABEL, CHECKBOX, FIELD, OUTLINE, PRIMARY } from './importStyles';

const LABEL = 'block text-sm text-base-content mb-1';
const NUM = 'text-right tabular-nums';

export default function TimedOpeningPanel() {
  const { t } = useTranslation('member');
  const [files, setFiles] = useState<File[]>([]);
  const [preview, setPreview] = useState<MemberOpeningPreview | null>(null);
  const [decisions, setDecisions] = useState<Record<string, MemberOpeningDecision>>({});
  const [fresh, setFresh] = useState(false);
  const [result, setResult] = useState<MemberOpeningResult | null>(null);
  const [error, setError] = useState('');
  const form = () => {
    const data = new FormData();
    files.forEach((file) => data.append('files', file));
    data.append(
      'payload',
      JSON.stringify({
        decisions: Object.values(decisions),
        ...(preview ? { planHash: preview.planHash } : {}),
      }),
    );
    return data;
  };
  const fail = (e: unknown) => {
    setFresh(false);
    const code = (e as { response?: { data?: { code?: string } } }).response?.data?.code;
    setError(
      code === 'OPENING_PLAN_STALE'
        ? t('opening.stale')
        : code === 'OPENING_PERIOD_EXPIRED'
          ? t('opening.expired')
          : t('opening.failed'),
    );
  };
  const dryRun = useMutation({
    mutationFn: () =>
      api.post<MemberOpeningPreview>('/members/import/timed-opening/preview', form(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: (value) => {
      setPreview(value);
      setFresh(true);
      setError('');
    },
    onError: fail,
  });
  const confirm = useMutation({
    mutationFn: () =>
      api.post<MemberOpeningResult>('/members/import/timed-opening/confirm', form(), {
        headers: { 'Content-Type': 'multipart/form-data' },
      }),
    onSuccess: (value) => {
      setResult(value);
      setError('');
    },
    onError: fail,
  });
  const pending = dryRun.isPending || confirm.isPending;
  const update = (key: string, decision: MemberOpeningDecision) => {
    setDecisions((previous) => ({ ...previous, [key]: decision }));
    setFresh(false);
  };
  return (
    <section className="min-w-0 w-full space-y-4" aria-label={t('opening.title')}>
      <h2 className="text-xl font-semibold text-base-content">{t('opening.title')}</h2>
      <p className="text-sm text-base-content">{t('opening.scope')}</p>
      <fieldset disabled={pending || Boolean(result)} className="min-w-0 w-full space-y-4">
        <label className="block">
          <span className={LABEL}>{t('opening.files')}</span>
          <input
            className="block w-full text-sm text-base-content file:mr-3 file:min-h-11 file:cursor-pointer file:border file:border-base-300 file:bg-base-100 file:px-4 file:text-sm file:font-medium file:text-base-content hover:file:bg-base-300"
            type="file"
            accept=".xlsx"
            multiple
            onChange={(event) => {
              setFiles(Array.from(event.target.files ?? []));
              setPreview(null);
              setDecisions({});
              setFresh(false);
              setError('');
            }}
          />
        </label>
        <button
          type="button"
          className={preview ? OUTLINE : PRIMARY}
          disabled={files.length !== 2}
          onClick={() => dryRun.mutate()}
        >
          {t('opening.preview')}
        </button>
        {preview && (
          <>
            <div className="overflow-x-auto">
              <table className="w-full min-w-[640px] text-left text-sm text-base-content [&_th]:p-2 [&_td]:p-2 [&_td]:whitespace-nowrap">
                <caption className="caption-top p-2 text-left text-sm text-base-content">
                  {t('opening.provenance')}
                </caption>
                <thead>
                  <tr className="border-b border-base-300">
                    {['period', 'rows', 'source', 'overlap', 'opening', 'expiry'].map((key) => (
                      <th
                        key={key}
                        scope="col"
                        className={`font-semibold ${['rows', 'source', 'overlap', 'opening'].includes(key) ? 'text-right' : ''}`}
                      >
                        {t(`opening.${key}`)}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {preview.periods.map((p) => (
                    <tr key={p.period} className="border-b border-base-300">
                      <td>{p.period}</td>
                      <td className={NUM}>{p.rows}</td>
                      <td className={NUM}>{p.source}</td>
                      <td className={NUM}>{p.overlap}</td>
                      <td className={NUM}>{p.opening}</td>
                      <td>
                        {p.lastValidDay}
                        <br />
                        {p.expiresAt}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
            {preview.rows.map((row) => {
              const key = `${row.sourceKey}|${row.period}`;
              const decision = decisions[key] ?? {
                sourceKey: row.sourceKey,
                period: row.period,
                state: 'NO_OVERLAP' as const,
                explanation: '',
                evidenceReference: '',
                evidenceDigest: '',
                sourceCutoff: preview.files.find((f) => f.period === row.period)!.exportedAt,
                attested: false,
                checkedLedgerIds: row.ledger.map((l) => l.id),
                allocations: [],
              };
              return (
                <details
                  key={key}
                  className="border border-base-300 px-3 text-sm text-base-content"
                  open={row.holds.length > 0}
                >
                  <summary className="flex min-h-11 cursor-pointer items-center py-2 tabular-nums">
                    {row.period} · {row.sourceKey} · {t(`opening.states.${row.state}`)} · C=
                    {row.sourceAmount} / B={row.beforeBalance} / O={row.opening} / B+O=
                    {row.resultingBalance}
                  </summary>
                  {row.holds.map((hold) => (
                    <p className={`${ALERT} mb-2`} key={hold}>
                      {t(`opening.holds.${hold}`, { defaultValue: t('opening.unresolved') })}
                    </p>
                  ))}
                  {row.ledger.length > 0 && (
                    <div className="space-y-3 pb-3">
                      <p>{t('opening.evidenceHelp')}</p>
                      <label className="block">
                        <span className={LABEL}>{t('opening.decision')}</span>
                        <select
                          className={`${FIELD} max-w-sm`}
                          value={decisions[key]?.state ?? ''}
                          onChange={(e) => {
                            if (!e.target.value) {
                              setDecisions((previous) => {
                                const next = { ...previous };
                                delete next[key];
                                return next;
                              });
                              setFresh(false);
                            } else
                              update(key, {
                                ...decision,
                                state: e.target.value as MemberOpeningDecision['state'],
                                allocations: [],
                              });
                          }}
                        >
                          <option value="">{t('opening.states.HOLD')}</option>
                          <option value="NO_OVERLAP">{t('opening.states.NO_OVERLAP')}</option>
                          <option value="PROVEN_OVERLAP">
                            {t('opening.states.PROVEN_OVERLAP')}
                          </option>
                        </select>
                      </label>
                      {(['explanation', 'evidenceReference', 'evidenceDigest'] as const).map(
                        (field) => (
                          <label className="block" key={field}>
                            <span className={LABEL}>{t(`opening.${field}`)}</span>
                            <input
                              className={FIELD}
                              maxLength={
                                field === 'explanation'
                                  ? 1000
                                  : field === 'evidenceDigest'
                                    ? 64
                                    : 255
                              }
                              value={decision[field]}
                              onChange={(e) =>
                                update(key, { ...decision, [field]: e.target.value })
                              }
                            />
                          </label>
                        ),
                      )}
                      <p className="tabular-nums">
                        {t('opening.cutoff')}: {decision.sourceCutoff}
                      </p>
                      <div className="overflow-x-auto">
                        <table className="w-full text-left [&_th]:p-2 [&_td]:p-2">
                          <caption className="caption-top p-2 text-left font-semibold">
                            {t('opening.ledger')} · {row.sourceKey}
                          </caption>
                          <thead>
                            <tr className="border-b border-base-300">
                              <th scope="col" className="font-semibold">
                                {t('opening.ledger')}
                              </th>
                              <th scope="col" className="font-semibold text-right">
                                {t('opening.amount')}
                              </th>
                              <th scope="col" className="font-semibold">
                                {t('opening.allocation')}
                              </th>
                            </tr>
                          </thead>
                          <tbody>
                            {row.ledger.map((entry) => (
                              <tr key={entry.id} className="border-b border-base-300">
                                <td className="break-all">
                                  {entry.id}
                                  <br />
                                  {entry.source} · {entry.createdAt}
                                </td>
                                <td className={NUM}>{entry.delta}</td>
                                <td>
                                  {decision.state === 'PROVEN_OVERLAP' &&
                                    Number(entry.delta) > 0 && (
                                      <input
                                        className={`${FIELD} w-28`}
                                        aria-label={`${t('opening.allocation')} ${entry.id}`}
                                        type="number"
                                        min="0"
                                        step="1"
                                        value={
                                          decision.allocations.find((a) => a.ledgerId === entry.id)
                                            ?.amount ?? ''
                                        }
                                        onChange={(e) =>
                                          update(key, {
                                            ...decision,
                                            allocations: [
                                              ...decision.allocations.filter(
                                                (a) => a.ledgerId !== entry.id,
                                              ),
                                              ...(e.target.value
                                                ? [{ ledgerId: entry.id, amount: e.target.value }]
                                                : []),
                                            ],
                                          })
                                        }
                                      />
                                    )}
                                </td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <label className={`${CHECK_LABEL} items-start py-3`}>
                        <input
                          type="checkbox"
                          className={CHECKBOX}
                          checked={decision.attested}
                          onChange={(e) => update(key, { ...decision, attested: e.target.checked })}
                        />
                        {t('opening.attestation')}
                      </label>
                    </div>
                  )}
                </details>
              );
            })}
            {!fresh && (
              <p
                role="status"
                className="border border-warning px-3 py-2 text-sm text-base-content"
              >
                {t('opening.repreview')}
              </p>
            )}
            <button
              type="button"
              className={PRIMARY}
              disabled={!fresh || !preview.ready}
              onClick={() => confirm.mutate()}
            >
              {t('opening.confirm')}
            </button>
          </>
        )}
      </fieldset>
      {pending && (
        <p role="status" className="text-sm text-base-content">
          {t('opening.pending')}
        </p>
      )}
      {error && (
        <p role="alert" className={ALERT}>
          {error}
        </p>
      )}
      {result && (
        <p role="status" className="border border-success p-3 text-sm text-base-content">
          {t(result.alreadyConfirmed ? 'opening.already' : 'opening.done')} {result.batchId}
        </p>
      )}
    </section>
  );
}
